/**
 * What to tell a user whose sample the loader refused.
 *
 * The loader's reason is always shown verbatim; this adds what to do about it.
 * Matched on how the reason starts, following the refusal table in the
 * supplied-sample spec (§5.6).
 */

export type RefusalKind =
    | 'tier_mapping' | 'identifier_column' | 'duplicate_alert' | 'blank_identifier'
    | 'conflicting_columns' | 'drafts' | 'json_shape' | 'file_type' | 'kyc' | 'other';

export interface RefusalGuidance {
    kind: RefusalKind;
    advice: string;
}

const RULES: [RegExp, RefusalKind, string][] = [
    [/no approved 'priority' tier mapping/i, 'tier_mapping',
        "Your organisation's tier ladder has not been confirmed. An administrator must approve how your tiers map onto the platform's before any benchmark can run."],
    [/proposed for required field\(s\) and saved as drafts/i, 'drafts',
        'Some columns were recognised by the model but must be confirmed by a person before the sample can run. Confirm them below, then preview again.'],
    [/supplied kyc|kyc.*more than one record for|none of the .* customer\(s\)|no column identified as \['customer_id'\]/i, 'kyc',
        "Fix the KYC file: one row per customer, keyed by the same identifier as the alerts' customer column."],
    [/has no column identified as/i, 'identifier_column',
        'Rename the column to a recognised name (for example alert_ref or scenario_id), or ask an administrator to map it.'],
    [/repeats \d+ alert_ref value/i, 'duplicate_alert',
        'Each alert must appear once in the file.'],
    [/have an empty alert_ref or scenario_id/i, 'blank_identifier',
        'Fill in or remove the rows with no alert reference or scenario.'],
    [/more than one supplied column maps onto the same field/i, 'conflicting_columns',
        'Keep one identifier column and remove the other.'],
    [/must be a list of alert objects/i, 'json_shape',
        "The file's shape is wrong: it must be a list of alert objects, or an object with an 'alerts' list."],
    [/is not a \.csv, \.json or \.jsonl file/i, 'file_type',
        'Convert the file to CSV, JSON or JSONL.']
];

export function refusalGuidance(reason: string | null | undefined): RefusalGuidance {
    const text = reason ?? '';
    for (const [pattern, kind, advice] of RULES) {
        if (pattern.test(text)) return { kind, advice };
    }
    return { kind: 'other', advice: 'Correct the file and preview it again.' };
}

/**
 * Which file's columns a "saved as drafts" refusal is about: the loader
 * prefixes KYC ones with "KYC column(s)".
 */
export function draftRelation(reason: string | null | undefined): 'case_history_alert' | 'customer_master' {
    return /^\s*KYC column\(s\)/i.test(reason ?? '') ? 'customer_master' : 'case_history_alert';
}
